import { buildFaqSchema } from './structuredData';

export const SEO_CONTENT = {
  '/': {
    buttonLabel: 'Learn More',
    title: 'MyRoomsAI: All-in-One AI Home Design Platform',
    intro:
      'MyRoomsAI brings interior AI, exterior AI, garden AI, and floorplan AI together in one place so you can explore realistic room generation without switching tools. Whether you want to redesign a room with AI, refresh curb appeal, or turn a 2D layout into a visual concept, the platform is built for homeowners, renters, and design professionals who need fast, photo-based previews.',
    sections: [
      {
        heading: 'Interior and Room Redesign with AI',
        paragraphs: [
          'Upload a photo of any room and use our room designer AI to explore new furniture layouts, color palettes, and decoration styles. The AI room redesign workflow keeps your room proportions recognizable while showing ai generated rooms you can compare side by side before spending on paint, flooring, or furniture.',
          'From bedrooms and living rooms to kitchens and home offices, MyRoomsAI helps you preview multiple directions quickly. Many users treat it as their my ai room designer for early planning, mood boards, and client presentations.',
        ],
        list: [
          'AI room decoration for full interior style changes',
          'Furniture replacement previews without moving real pieces',
          'Room-type guides for bedrooms, kitchens, living rooms, and offices',
        ],
      },
      {
        heading: 'Exterior, Garden, and Floorplan Tools',
        paragraphs: [
          'Beyond interiors, MyRoomsAI supports ai home design for the full property. Exterior AI tools visualize facade upgrades, while garden AI helps you plan patios, planting, and outdoor living areas from a real yard photo.',
          'Floorplan AI tools let you move from a simple layout sketch to a 2D floor plan to 3D preview or a 2D floor plan to realistic image of individual rooms. Together, these workflows support renovation planning, listings, and new-build visualization.',
        ],
        list: [
          'Exterior facade and curb-appeal concepts',
          'Garden and landscaping previews from outdoor photos',
          '2D floor plan editor concepts and plan-to-render workflows',
        ],
      },
      {
        heading: 'Why Choose an Integrated Platform',
        paragraphs: [
          'Instead of juggling separate apps for each task, MyRoomsAI connects related workflows. Start with ai room decorator ideas indoors, then explore how the exterior or garden could complement the same project. Real before-and-after examples on the site show what realistic room generation looks like in practice.',
        ],
      },
    ],
    faq: [
      {
        q: 'How does AI room redesign work on MyRoomsAI?',
        a: 'You upload a clear room photo, choose a room type and design style, and the AI generates a redesigned preview based on your image and preferences. You can generate multiple variations before committing to purchases or renovation work.',
      },
      {
        q: 'Can AI generate realistic room images from my photos?',
        a: 'Yes. MyRoomsAI is designed to produce realistic room concepts that preserve the general layout of your space while applying new furniture, colors, and styling directions you select.',
      },
      {
        q: 'Does MyRoomsAI cover more than interior rooms?',
        a: 'Yes. The platform includes exterior design, garden design, and floorplan visualization tools alongside interior room decoration and furniture replacement.',
      },
      {
        q: 'Who is MyRoomsAI for?',
        a: 'Homeowners, renters, interior enthusiasts, real estate professionals, and small design teams who want fast visual previews without advanced 3D modeling software.',
      },
    ],
  },

  '/ai-tools/ai-room-decoration': {
    buttonLabel: 'About This AI Tool',
    title: 'AI Room Decoration and Redesign Guide',
    intro:
      'The AI Room Decoration tool helps you redesign room layouts and styles from a single photo. If you are searching for a room designer AI that shows ideas in your actual space—not a generic stock room—this workflow is built for that purpose.',
    sections: [
      {
        heading: 'What You Can Do with AI Room Decoration',
        paragraphs: [
          'Use this tool to explore full decoration concepts: new furniture arrangements, wall colors, lighting mood, and interior style direction. It is ideal when you want to redesign room ai previews before buying decor or hiring a contractor.',
          'The AI generated rooms keep your camera angle and room shape intact, which makes comparisons more useful than starting from a blank template.',
        ],
        list: [
          'Decorate bedrooms, living rooms, kitchens, offices, and more',
          'Compare multiple styles on the same photo',
          'Add custom notes for furniture or color direction',
        ],
      },
      {
        heading: 'Best Practices for Strong Results',
        paragraphs: [
          'Take your photo in daylight from a corner or doorway so walls, floor, and main furniture are visible. Choose a room type that matches the space, then experiment with styles before settling on one direction.',
          'Many users generate several ai room decorator variations and share the best previews with family, landlords, or design partners.',
        ],
      },
      {
        heading: 'When to Use Room Decoration vs. Other Tools',
        paragraphs: [
          'Choose room decoration when you want a broad interior refresh. If you mainly want to swap furniture while keeping the rest of the room similar, try the furniture replacement tool. For layout planning from plans rather than photos, explore the floorplan tools.',
        ],
      },
    ],
    faq: [
      {
        q: 'How does AI room redesign work with a photo upload?',
        a: 'Upload a clear room photo, select room type and style, and the AI produces a decorated version of your space. You can adjust settings and regenerate until you find a direction you like.',
      },
      {
        q: 'Can I redesign bedrooms using AI with this tool?',
        a: 'Yes. Bedroom is one of the supported room types, and you can also visit the dedicated bedroom room-type guide for extra tips.',
      },
      {
        q: 'Will the AI change my room layout completely?',
        a: 'The tool aims to preserve recognizable proportions while applying new decoration. You control style, colors, and notes to guide how bold the transformation appears.',
      },
      {
        q: 'Can AI generate realistic room images I can share?',
        a: 'Yes. Results are intended as realistic visual concepts you can use for planning, inspiration, or client discussions depending on your plan terms.',
      },
    ],
  },

  '/ai-tools/ai-3d-model-to-realistic-interior': {
    buttonLabel: 'About This AI Tool',
    title: 'AI 3D Model to Realistic Interior Image Guide',
    intro:
      'The AI 3D Model to Realistic Interior Image tool converts 3D interior model screenshots from Blender, 3ds Max, Maya, SketchUp, Three.js, or any 3D viewer into photorealistic interior photographs. Unlike room decoration from a real photo, this workflow treats your upload as a modeled architectural reference and preserves the same space, camera angle, and openings.',
    sections: [
      {
        heading: 'What You Can Do with 3D Model to Realistic Interior',
        paragraphs: [
          'Use this tool for AI interior rendering when you already have a 3D model viewport screenshot and need a realistic interior design image for presentations, client reviews, listings, or design exploration. The AI improves materials, lighting, furniture, textures, and staging while keeping the modeled room geometry intact.',
          'This is ideal for architects, interior designers, visualization artists, and homeowners who work in 3D software and want a photorealistic interior photograph without rebuilding the scene manually.',
        ],
        list: [
          'Convert Blender, SketchUp, 3ds Max, Maya, or Three.js interior screenshots to realistic images',
          'Preserve room dimensions, proportions, camera viewpoint, doors, and windows',
          'Apply room type, style, decoration level, colors, and custom notes',
        ],
      },
      {
        heading: 'Best Practices for Strong Results',
        paragraphs: [
          'Capture a clear viewport screenshot with the full room visible, minimal software UI overlays, and a stable camera angle. Choose a room type and interior style that match your design intent, then use notes to guide furniture, materials, or lighting direction.',
          'For spacious modeled rooms, keep the decoration level and notes aligned with the scale shown in the 3D screenshot so the AI maintains believable proportions and circulation.',
        ],
      },
      {
        heading: 'When to Use This Tool vs. Other Interior Tools',
        paragraphs: [
          'Choose this tool when your input is a 3D model screenshot, not a real room photo. If you already have a photographed room, use the AI Room Decoration tool. If you are working from a 2D floor plan, explore the floor plan to 3D or floor plan to room render tools instead.',
        ],
      },
    ],
    faq: [
      {
        q: 'Can I convert a Blender interior to a realistic image?',
        a: 'Yes. Upload a viewport screenshot from Blender or any supported 3D software. The AI treats it as a modeled space and generates a photorealistic interior photograph while preserving structure and camera angle.',
      },
      {
        q: 'Will the AI change my room dimensions or window positions?',
        a: 'The tool is designed to preserve modeled room geometry, proportions, camera viewpoint, and visible openings. Use notes only when you explicitly want a specific change.',
      },
      {
        q: 'Does this work with SketchUp and 3ds Max screenshots?',
        a: 'Yes. Screenshots from SketchUp, 3ds Max, Maya, Three.js, and other 3D interior model viewers are supported.',
      },
      {
        q: 'Is the output a CGI render or a realistic interior photograph?',
        a: 'The goal is a realistic professional interior photograph of the same modeled space—not another viewport render, game screenshot, or soft CGI look.',
      },
    ],
  },

  '/ai-tools/ai-furniture-replacement': {
    buttonLabel: 'About This AI Tool',
    title: 'AI Furniture Replacement and Room Remodeling',
    intro:
      'The AI Furniture Replacement tool focuses on swapping furniture and key pieces in your existing room photo. It is a practical way to preview remodeling decisions before you move, buy, or stage real items.',
    sections: [
      {
        heading: 'Furniture Replacement Without Guesswork',
        paragraphs: [
          'When you want to redesign room ai concepts around new sofas, beds, tables, or storage, this tool keeps the room structure familiar while updating the furniture story. That makes it easier to judge scale, style fit, and layout changes.',
          'Use it as an ai room decorator for targeted refreshes rather than a full decoration overhaul.',
        ],
        list: [
          'Preview new furniture in your actual room photo',
          'Keep walls and architecture recognizable',
          'Test styles before purchasing or moving pieces',
        ],
      },
      {
        heading: 'Who Benefits Most',
        paragraphs: [
          'Renters planning reversible upgrades, homeowners comparing sofa or bed options, and stagers preparing listing photos all benefit from fast furniture previews. The workflow is especially helpful when shopping online and trying to imagine scale in your space.',
        ],
      },
      {
        heading: 'Tips for Better Furniture Previews',
        paragraphs: [
          'Photograph the room with existing furniture visible and evenly lit. Add notes describing the pieces you want replaced or the style direction you prefer. Generate multiple variations to compare layout and tone.',
        ],
      },
    ],
    faq: [
      {
        q: 'How is furniture replacement different from full room decoration?',
        a: 'Furniture replacement focuses on updating key pieces and layout feel while keeping much of the room context similar. Full room decoration explores broader style, color, and decor changes.',
      },
      {
        q: 'Can I use custom notes for specific furniture items?',
        a: 'Yes. You can describe the furniture direction or pieces you want considered, which helps guide the AI output.',
      },
      {
        q: 'Does this work for kitchens and offices?',
        a: 'Yes. Select the matching room type when uploading your photo to get results tuned to that space.',
      },
    ],
  },

  '/ai-tools/ai-exterior-design': {
    buttonLabel: 'About This AI Tool',
    title: 'AI Home Exterior Design Guide',
    intro:
      'The AI Exterior Home Design tool transforms building photos into facade redesign previews. It supports ai home exterior design planning for curb appeal, renovation mood boards, and material direction before construction starts.',
    sections: [
      {
        heading: 'Exterior Design AI for Real Homes',
        paragraphs: [
          'Upload a clear photo of your house or building exterior and explore new facade styles, materials, colors, and environmental settings. The exterior design ai workflow preserves main structural elements like windows, doors, and proportions so results feel grounded in your property.',
          'Use it as an ai exterior design generator when comparing modern, traditional, or regional style directions.',
        ],
        list: [
          'Facade style and material exploration',
          'Weather, lighting, and time-of-day options',
          'Renovation previews that respect building structure',
        ],
      },
      {
        heading: 'Planning Renovations with AI',
        paragraphs: [
          'Exterior upgrades are expensive and hard to undo. AI previews help homeowners and contractors align on direction early. Pair exterior concepts with garden design tools when planning full property transformations.',
        ],
      },
      {
        heading: 'Photography Tips',
        paragraphs: [
          'Shoot the full facade straight-on or at a slight angle with minimal obstructions. Daylight photos with clear sky or soft cloud cover usually produce the most readable material and color changes.',
        ],
      },
    ],
    faq: [
      {
        q: 'Can AI redesign my home exterior from a phone photo?',
        a: 'Yes, as long as the photo is clear and shows the main facade. Better lighting and a straight-on view improve results.',
      },
      {
        q: 'Will the AI change window and door positions?',
        a: 'The tool is designed to preserve major structural elements while changing style, materials, and surface treatments.',
      },
      {
        q: 'Is this useful for real estate listings?',
        a: 'Many users create concept previews to discuss renovation potential with buyers or to plan listing improvements, subject to your usage terms.',
      },
    ],
  },

  '/ai-tools/ai-garden-design': {
    buttonLabel: 'About This AI Tool',
    title: 'AI Garden and Landscaping Design',
    intro:
      'The AI Garden Design tool helps you visualize outdoor upgrades from a real yard, patio, or garden photo. It extends ai home design beyond walls to planting, paths, seating areas, and landscape character.',
    sections: [
      {
        heading: 'Landscape Ideas from Your Outdoor Photo',
        paragraphs: [
          'Upload a photo of your backyard, front garden, terrace, or patio and explore landscaping directions before digging, planting, or buying hardscape materials. The AI uses your existing outdoor context to suggest layout and style changes.',
        ],
        list: [
          'Backyard and front garden concepts',
          'Patio and terrace styling previews',
          'Planting and path direction exploration',
        ],
      },
      {
        heading: 'Pair Garden Design with Exterior Tools',
        paragraphs: [
          'Properties often need coordinated exterior and garden updates. Many users generate garden previews alongside facade concepts to ensure the overall ai home design story feels cohesive.',
        ],
      },
      {
        heading: 'Getting Useful Garden Previews',
        paragraphs: [
          'Capture the full area you want to change, avoid heavy shadows, and add notes about features you want—seating, paths, planting density, or low-maintenance goals.',
        ],
      },
    ],
    faq: [
      {
        q: 'Can AI design a garden from my existing yard photo?',
        a: 'Yes. The tool uses your uploaded outdoor photo as the base and applies landscaping style directions you choose.',
      },
      {
        q: 'Does this replace a landscape architect?',
        a: 'It is a visual planning aid for early ideas. Complex grading, drainage, or structural outdoor work still requires professional assessment.',
      },
      {
        q: 'What outdoor spaces are supported?',
        a: 'Backyards, front gardens, patios, terraces, and similar outdoor areas work well when photographed clearly.',
      },
    ],
  },

  '/ai-tools/ai-2d-floorplan-creator': {
    buttonLabel: 'AI Design Guide',
    title: 'Free 2D Floor Plan Designer Concepts with AI',
    intro:
      'The AI Floor Plan Creator helps you describe a home layout and explore a generated 2D floor plan concept. It is useful as a free 2d floor plan designer starting point when you need early layout ideas before detailed drafting.',
    sections: [
      {
        heading: 'Describe Your Layout, Preview a Plan',
        paragraphs: [
          'Enter room counts, size goals, and layout preferences to generate a 2d floor plan design with ai assistance. This is ideal for early renovation brainstorming, rental reconfiguration ideas, or new-build planning conversations.',
          'Think of it as a free 2d floor plan editor companion for concept generation rather than CAD-level precision.',
        ],
        list: [
          'Room count and layout brief input',
          'Early 2D concept generation',
          'Starting point for 3D and realistic room renders',
        ],
      },
      {
        heading: 'Connect to Other Floorplan Workflows',
        paragraphs: [
          'After you have a layout direction, use the 2D floor plan to 3D tool or the floor plan to realistic room generator to visualize spaces in more detail. MyRoomsAI connects planning and visualization in one platform.',
        ],
      },
      {
        heading: 'Best Use Cases',
        paragraphs: [
          'Homeowners comparing open-plan vs. divided layouts, small developers exploring unit configurations, and design enthusiasts sketching dream homes all benefit from fast AI layout concepts.',
        ],
      },
    ],
    faq: [
      {
        q: 'Is the floorplan editor free to try?',
        a: 'MyRoomsAI uses a credit-based model. You can explore pricing to see current access options for floor plan tools.',
      },
      {
        q: 'Can I export CAD-ready files from this tool?',
        a: 'This tool focuses on AI-generated layout concepts. For precise CAD output, use the result as a reference in dedicated drafting software.',
      },
      {
        q: 'Can I turn the plan into a 3D preview?',
        a: 'Yes. Upload or continue with the 2D floor plan to 3D transformer and room render tools for deeper visualization.',
      },
    ],
  },

  '/ai-tools/ai-2d-floorplan-designer': {
    buttonLabel: 'AI Design Guide',
    title: 'Draw 2D Floor Plans Online with MyRoomsAI',
    intro:
      'The AI 2D Floor Plan Designer is a browser-based manual drawing tool for creating walls, rooms, doors, and windows. Use it as a free 2d floor plan designer when you want to sketch layouts from scratch before turning them into 3D previews or realistic room renders.',
    sections: [
      {
        heading: 'Manual Drawing With Shared Editor Tools',
        paragraphs: [
          'Start with an empty canvas and draw walls with snapping, node editing, and wall splitting. Add doors and windows, detect closed rooms, and preview the layout in 3D—all using the same shared floor plan editor used across MyRoomsAI.',
          'No image upload is required to begin. The designer opens ready for manual layout work.',
        ],
        list: [
          'Wall drawing with horizontal and vertical snapping',
          'Door and window placement',
          'Room detection and selection',
          'Built-in 3D preview',
        ],
      },
      {
        heading: 'Export and Continue With Other Tools',
        paragraphs: [
          'Export your floor plan JSON from the sidebar when you are ready to share or continue. Use the 2D floor plan to 3D transformer or floor plan to realistic room generator to visualize your layout in more detail.',
        ],
      },
      {
        heading: 'Best Use Cases',
        paragraphs: [
          'Homeowners sketching renovation layouts, renters planning furniture placement, and design enthusiasts exploring room configurations all benefit from a fast manual 2D floor plan editor.',
        ],
      },
    ],
    faq: [
      {
        q: 'Do I need to upload a floor plan image?',
        a: 'No. The designer starts with an empty canvas for manual drawing. You can draw walls and rooms from scratch.',
      },
      {
        q: 'Can I preview my plan in 3D?',
        a: 'Yes. Use the 2D/3D toggle in the editor to preview your drawn layout in 3D without leaving the page.',
      },
      {
        q: 'Can I use my plan with other MyRoomsAI tools?',
        a: 'Yes. Export your plan JSON or continue with the 2D to 3D transformer and room render tools for deeper visualization.',
      },
    ],
  },

  '/ai-tools/ai-2d-floorplan-to-3d': {
    buttonLabel: 'About This AI Tool',
    title: 'AI 2D Floor Plan to 3D Conversion Guide',
    intro:
      'The AI 2D Floor Plan to 3D Transformer turns uploaded floor plan images into open-top 3D-style home previews. Upload your plan, choose render style, furnishing, camera angle, interior style, and image quality, then generate a spatial visualization without manual 3D modeling.',
    sections: [
      {
        heading: 'From Flat Plan to 3D Preview',
        paragraphs: [
          'Upload a clear 2D floor plan image and set Render Style (Realistic, Sketch, Hand Drawing, and more), Furnishing (Furnished or Unfurnished), 3D View Angle (corner, front, top-down, and cutaway views), Interior Style, and optional notes. The tool prepares your plan and sends your settings to the AI generator.',
        ],
        list: [
          'Upload floor plan images in supported formats',
          'Render style, furnishing, camera angle, and interior style controls',
          'Open-top 3D previews for layout planning and presentations',
        ],
      },
      {
        heading: 'Who Uses Plan-to-3D Previews',
        paragraphs: [
          'Real estate marketers, interior designers, architects in early phases, and homeowners planning extensions use 3D plan previews to communicate room flow, proportions, and layout intent before construction or staging.',
        ],
      },
      {
        heading: 'Prepare Your Floor Plan Image',
        paragraphs: [
          'Use a high-contrast plan with readable walls, doors, and room labels. Crop unnecessary margins and keep room boundaries clear. Better source plans produce cleaner 3D outputs. Use Additional notes to describe colors, flooring, or furniture direction.',
        ],
      },
    ],
    faq: [
      {
        q: 'Can I convert a 2D floor plan into 3D with any image?',
        a: 'Yes, if the plan is readable and in a supported image format. Clear wall lines, doors, and labels improve results.',
      },
      {
        q: 'What render styles and view angles are available?',
        a: 'Choose from Realistic, Sketch, Hand Drawing, Pencil-3D, Anime Style, and Marker render styles. Camera options include left and right corner views, front view, top-down view, and cutaway angles.',
      },
      {
        q: 'Is this the same as a full BIM model?',
        a: 'No. It produces a visual 3D-style preview image for planning and presentation, not a construction BIM model.',
      },
      {
        q: 'Can I render individual rooms realistically?',
        a: 'For room-level realistic interiors from a selected floor plan area, use the AI Floor Plan to Realistic Room Generator tool.',
      },
    ],
  },

  '/ai-tools/ai-2d-floorplan-to-room-render': {
    buttonLabel: 'AI Design Guide',
    title: '2D Floor Plan to Realistic Room Images',
    intro:
      'The AI Floor Plan to Realistic Room Generator turns a selected area of your floor plan into a realistic interior preview. It is designed for 2d floor plan to realistic image workflows when you want to see how a specific room could look when built or staged.',
    sections: [
      {
        heading: 'Select a Room, Generate a Realistic Interior',
        paragraphs: [
          'Upload your plan, frame the target room, and choose room type plus interior style. The AI focuses on that area to produce a realistic room concept tied to your layout rather than a generic stock photo.',
        ],
        list: [
          'Room-area selection from 2D plans',
          'Realistic interior styling by room type',
          'Useful for new builds and renovations',
        ],
      },
      {
        heading: 'Plan Listings and Client Presentations',
        paragraphs: [
          'Developers and agents use realistic room renders to help buyers imagine unfinished spaces. Designers use them to align clients on style before specifying finishes and furniture.',
        ],
      },
      {
        heading: 'Workflow Tips',
        paragraphs: [
          'Frame only the room you want rendered, label boundaries clearly in the source plan when possible, and generate multiple style directions to compare options.',
        ],
      },
    ],
    faq: [
      {
        q: 'Can AI generate realistic room images from a floor plan?',
        a: 'Yes. Select the room area, choose type and style, and the tool generates an interior preview based on that section of your plan.',
      },
      {
        q: 'Do I need a photo of the finished room?',
        a: 'No. This workflow starts from a floor plan, which is ideal for unbuilt or empty spaces.',
      },
      {
        q: 'Can I combine this with 3D plan views?',
        a: 'Yes. Many users create a 3D plan overview first, then realistic room renders for key spaces.',
      },
    ],
  },

  '/room-types/bedroom': {
    buttonLabel: 'AI Design Guide',
    title: 'AI Bedroom Design Ideas and Planning',
    intro:
      'Bedroom design by ai helps you explore layouts, color palettes, and furniture choices before you shop or renovate. Whether you want a calm minimalist retreat or a bold statement room, ai bedroom previews make decisions easier.',
    sections: [
      {
        heading: 'AI Generated Bedroom Concepts',
        paragraphs: [
          'Upload a photo of your bedroom and generate ai generated bedroom directions in styles like modern, Scandinavian, coastal, or luxury. Compare options on the same photo to see what fits your space and light conditions.',
        ],
        list: [
          'Bed layout and furniture scale previews',
          'Color and textile direction exploration',
          'Multiple style comparisons on one photo',
        ],
      },
      {
        heading: 'Bedroom Design by AI for Renters and Owners',
        paragraphs: [
          'Renters can preview reversible decor changes; owners can plan larger upgrades with clearer vision. Use the ai bedroom workflow to align with partners or contractors before purchasing beds, wardrobes, or lighting.',
        ],
      },
      {
        heading: 'Recommended Next Steps',
        paragraphs: [
          'After exploring bedroom ideas here, open the AI Room Decoration tool with bedroom selected for full redesign previews, or browse real transformations for inspiration.',
        ],
      },
    ],
    faq: [
      {
        q: 'Can I redesign bedrooms using AI from a phone photo?',
        a: 'Yes. Use a clear bedroom photo and select bedroom as the room type in the decoration or furniture tools.',
      },
      {
        q: 'Will small bedrooms work with AI design?',
        a: 'Yes. Corner or doorway photos that show most of the room produce helpful scale-aware previews.',
      },
      {
        q: 'Can I get ai generated bedroom results in multiple styles?',
        a: 'Yes. Generate several variations with different styles or notes to compare directions.',
      },
    ],
  },

  '/room-types/living-room': {
    buttonLabel: 'AI Design Guide',
    title: 'AI Living Room Design Guide',
    intro:
      'Living rooms are central to how a home feels. AI living room design lets you test layouts, sofas, media walls, and color stories on your actual space using living room interior design ai previews.',
    sections: [
      {
        heading: 'Explore Layout and Style Directions',
        paragraphs: [
          'Use ai living room design workflows to compare conversational layouts, open-plan furniture groupings, and style directions from modern to traditional. Seeing ideas in your room reduces costly trial-and-error shopping.',
        ],
        list: [
          'Sofa and seating layout previews',
          'Media wall and fireplace surround ideas',
          'Color and lighting mood exploration',
        ],
      },
      {
        heading: 'Living Room Interior Design AI for Entertaining',
        paragraphs: [
          'If you host often, preview traffic flow and seating capacity changes before moving heavy furniture. Living room interior design ai helps you validate whether a sectional, pair of sofas, or alternative layout fits best.',
        ],
      },
      {
        heading: 'Start Your Living Room Redesign',
        paragraphs: [
          'Jump into the AI Room Decoration tool with living room selected, or review the living room gallery and real transformations for proven before-and-after examples.',
        ],
      },
    ],
    faq: [
      {
        q: 'Can AI redesign an open-plan living and dining area?',
        a: 'Yes. Capture as much of the connected space as possible in one photo for cohesive previews.',
      },
      {
        q: 'Does this work for rental living rooms?',
        a: 'Yes. Preview reversible decor and furniture changes before buying or assembling pieces.',
      },
      {
        q: 'How do I get the most realistic living room results?',
        a: 'Use even lighting, show main walls and floor, and generate multiple style variations to compare.',
      },
    ],
  },

  '/room-types/kitchen': {
    buttonLabel: 'AI Design Guide',
    title: 'AI Kitchen Design from Photo',
    intro:
      'Kitchen renovations are high stakes. AI kitchen design from photo workflows let you preview cabinet tones, backsplash direction, and layout feel before committing to costly changes.',
    sections: [
      {
        heading: 'Preview Kitchen Changes Before You Renovate',
        paragraphs: [
          'Upload a kitchen photo and explore style directions while keeping the general layout readable. AI kitchen design from photo previews help you discuss options with family, contractors, or cabinet suppliers with a shared visual reference.',
        ],
        list: [
          'Cabinet color and finish direction',
          'Backsplash and wall treatment ideas',
          'Overall kitchen style exploration',
        ],
      },
      {
        heading: 'When AI Kitchen Previews Help Most',
        paragraphs: [
          'Cosmetic refreshes, rental-friendly updates, and pre-renovation mood boards benefit most. Structural moves like relocating plumbing may still require professional plans beyond AI visualization.',
        ],
      },
      {
        heading: 'Combine with Other MyRoomsAI Tools',
        paragraphs: [
          'Pair kitchen previews with living room or dining decoration concepts when updating connected spaces for a cohesive home story.',
        ],
      },
    ],
    faq: [
      {
        q: 'Can I redesign a kitchen from a single photo?',
        a: 'Yes. A clear photo showing cabinets, counters, and main walls works well for style and color exploration.',
      },
      {
        q: 'Will AI move my appliances or plumbing?',
        a: 'The tool focuses on visual style and decor direction while keeping the general layout recognizable unless you guide otherwise in notes.',
      },
      {
        q: 'Is this useful before a full remodel?',
        a: 'Yes. Many users build a visual brief before meeting contractors or cabinet designers.',
      },
    ],
  },

  '/room-types/office': {
    buttonLabel: 'AI Design Guide',
    title: 'AI Office and Workspace Design',
    intro:
      'Productive workspaces need good layout, lighting, and storage. AI home design tools from MyRoomsAI help you preview office and study setups that fit your room, equipment, and work style.',
    sections: [
      {
        heading: 'Home Office Layout Previews',
        paragraphs: [
          'Upload a photo of your office, study, or workspace corner and explore desk placement, shelving, and style direction. AI previews help remote workers and creators optimize focus and storage without endless rearranging.',
        ],
        list: [
          'Desk and monitor wall layouts',
          'Storage and shelving direction',
          'Calm vs. creative workspace styles',
        ],
      },
      {
        heading: 'Hybrid Rooms and Multi-Use Spaces',
        paragraphs: [
          'Guest room offices and living room work corners benefit from AI previews that show how furniture can serve dual purposes while still looking intentional.',
        ],
      },
      {
        heading: 'Getting Started',
        paragraphs: [
          'Use the AI Room Decoration tool with office selected, add notes about desk size or equipment needs, and compare minimalist vs. warm creative directions.',
        ],
      },
    ],
    faq: [
      {
        q: 'Can AI design a small home office nook?',
        a: 'Yes. Photograph the nook clearly and include notes about desk size or storage needs.',
      },
      {
        q: 'Does this work for corporate office planning?',
        a: 'It is best for home and small workspace visualization rather than large commercial floor planning.',
      },
      {
        q: 'Can I preview ergonomic layout changes?',
        a: 'You can explore desk and seating arrangements visually, though ergonomic assessment still requires human judgment.',
      },
    ],
  },

  '/real-transformations': {
    buttonLabel: 'Learn More',
    title: 'Real Before and After AI Transformations',
    intro:
      'The Real Transformations gallery showcases ai generated rooms and redesign room ai results from actual MyRoomsAI workflows. These examples demonstrate what realistic room generation looks like across bedrooms, kitchens, living rooms, gardens, and exteriors.',
    sections: [
      {
        heading: 'What You Will See in the Gallery',
        paragraphs: [
          'Each transformation pairs a before photo with an AI-generated after concept. Browse by room type or style to understand how different tools handle lighting, furniture scale, and architectural context.',
        ],
        list: [
          'Interior room decoration and furniture changes',
          'Kitchen, bedroom, and living room examples',
          'Exterior and garden transformation samples',
        ],
      },
      {
        heading: 'Learn from Real User-Style Outcomes',
        paragraphs: [
          'Use the gallery to set expectations before uploading your own photos. Notice which angles and lighting conditions produce the clearest results, then apply those tips to your projects.',
        ],
      },
      {
        heading: 'Create Your Own Transformation',
        paragraphs: [
          'Ready to try it yourself? Start with the AI Room Decoration tool, upload a clear photo, and generate your first preview. Compare your results to gallery examples to refine your settings.',
        ],
      },
    ],
    faq: [
      {
        q: 'Are these transformations real MyRoomsAI outputs?',
        a: 'Yes. The gallery shows before-and-after pairs produced through MyRoomsAI design workflows.',
      },
      {
        q: 'Can I get similar results in my room?',
        a: 'Results depend on photo quality, room type, and style settings. Clear photos and multiple generations improve outcomes.',
      },
      {
        q: 'Which tool was used for each example?',
        a: 'Examples may come from room decoration, furniture replacement, exterior, garden, or floorplan tools depending on the space shown.',
      },
    ],
  },

  '/pricing': {
    buttonLabel: 'Learn More',
    title: 'MyRoomsAI Pricing and Included AI Tools',
    intro:
      'MyRoomsAI pricing is built around credits that power ai home design workflows across interiors, exteriors, gardens, and floorplans. Understand what is included so you can choose the right access level for your projects.',
    sections: [
      {
        heading: 'What Credits Cover',
        paragraphs: [
          'Credits are consumed when you generate AI previews. Each tool—room decoration, furniture replacement, exterior design, garden design, and floorplan visualization—uses credits based on your selected quality and workflow settings.',
        ],
        list: [
          'Interior room decoration and furniture replacement',
          'Exterior facade and garden design previews',
          'Floor plan to 3D and realistic room rendering',
        ],
      },
      {
        heading: 'Choosing the Right Plan',
        paragraphs: [
          'Casual users exploring a single room may need fewer credits, while agents, designers, or renovation planners generating many variations benefit from larger packs. Credits do not expire, so you can work at your own pace.',
        ],
      },
      {
        heading: 'Value of an All-in-One Platform',
        paragraphs: [
          'Instead of paying for separate apps for each task, MyRoomsAI bundles related ai home design tools. Compare pricing against the cost of multiple subscriptions or manual visualization time.',
        ],
      },
    ],
    faq: [
      {
        q: 'Do credits expire?',
        a: 'Credits are purchased as one-time packs and do not expire, so you can use them when you need them.',
      },
      {
        q: 'Which tools use credits?',
        a: 'AI generation across room decoration, furniture replacement, exterior, garden, and floorplan tools consumes credits depending on settings.',
      },
      {
        q: 'Can I try MyRoomsAI before buying credits?',
        a: 'Check current signup offers and pricing on this page for the latest trial or starter options.',
      },
    ],
  },

  '/sitemap': {
    buttonLabel: 'Learn More',
    title: 'Explore MyRoomsAI: Site Overview',
    intro:
      'This sitemap lists public pages across MyRoomsAI—from ai home design tools and room-type guides to galleries, blog articles, and support pages. Use it to discover every major section of the platform.',
    sections: [
      {
        heading: 'Main Pages and Getting Started',
        paragraphs: [
          'Start on the home page for an overview of interior AI, exterior AI, garden AI, and floorplan AI. Visit Features, Pricing, FAQ, and Real Transformations to understand capabilities, costs, and example outputs before creating a project.',
        ],
      },
      {
        heading: 'AI Tools and Room Guides',
        paragraphs: [
          'AI tool pages cover room decoration, furniture replacement, exterior and garden design, and floorplan workflows including 2d floor plan design with ai. Room-type guides offer focused tips for bedrooms, living rooms, kitchens, and offices.',
        ],
        list: [
          'Seven public AI design tools',
          'Four room-type planning guides',
          'Galleries and blog for inspiration',
        ],
      },
      {
        heading: 'Galleries and Blog Resources',
        paragraphs: [
          'Browse design galleries and blog articles for deeper inspiration on topics like ai room designer basics, bedroom ideas, living room styling, and floorplan-to-room workflows.',
        ],
      },
    ],
    faq: [
      {
        q: 'Where do I start as a new visitor?',
        a: 'Begin on the home page, review Real Transformations for examples, then open the AI Room Decoration tool to upload your first photo.',
      },
      {
        q: 'Are blog and gallery pages part of MyRoomsAI?',
        a: 'Yes. They provide static inspiration and guides linked from this sitemap.',
      },
      {
        q: 'How often is the sitemap updated?',
        a: 'New public tools and guides are added as the platform grows. Check back for new entries.',
      },
    ],
  },
};

export function getSeoContent(pathname) {
  if (!pathname) return null;
  const cleanPath = pathname.replace(/\/+$/, '') || '/';
  return SEO_CONTENT[cleanPath] || null;
}

export function getSeoFaqSchema(pathname) {
  const content = getSeoContent(pathname);
  if (!content?.faq?.length) return null;
  return buildFaqSchema(content.faq);
}
